import apiClient from './client'

export interface LanConfig {
  subnet?: string
  gateway?: string
  vlan?: string
  dns?: string
  dhcp_range?: string
}

export interface ModbusConfig {
  baud_rate?: string
  parity?: 'none' | 'even' | 'odd'
  data_bits?: string
  stop_bits?: string
  protocol?: 'modbus_rtu' | 'modbus_ascii' | 'other'
}

export interface AudioConfig {
  signal_type?: 'analog_balanced' | 'analog_unbalanced' | 'digital_aes_ebu' | 'digital_dante' | 'other'
  connector_type?: 'xlr' | 'jack_3_5' | 'jack_6_3' | 'phoenix' | 'other'
}

export interface GenericConfig {
  custom_params?: string
}

export type NetworkConfig = LanConfig & ModbusConfig & AudioConfig & GenericConfig

export interface CommNetwork {
  id: number
  project_id: number
  network_type: string
  name: string
  code: string
  description?: string
  network_config?: Partial<NetworkConfig>
  schema_id?: number
  created_at?: string
  updated_at?: string
}

export const getProjectNetworks = async (projectId: number): Promise<CommNetwork[]> => {
  const response = await apiClient.get('/api/comm_networks', {
    params: { project_id: projectId }
  })
  if (Array.isArray(response.data)) {
    return response.data
  }
  if (response?.data && response.data.success) {
    return response.data.data
  }
  throw new Error('Failed to fetch project networks')
}

export const getNetwork = async (id: number): Promise<CommNetwork> => {
  const response = await apiClient.get(`/api/comm_networks/${id}`)
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data) {
    return response.data as unknown as CommNetwork
  }
  if (response?.data && response.data.success) {
    return response.data.data
  }
  throw new Error('Network not found')
}

export const createNetwork = async (
  data: Omit<CommNetwork, 'id' | 'created_at' | 'updated_at'>
): Promise<{ id: number }> => {
  const response = await apiClient.post('/api/comm_networks', data)
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data) {
    return response.data as { id: number }
  }
  if (response?.data && response.data.success) {
    return response.data.data
  }
  throw new Error('Failed to create network')
}

export const updateNetwork = async (
  id: number,
  data: Omit<Partial<CommNetwork>, 'id' | 'project_id' | 'created_at' | 'updated_at'>
): Promise<void> => {
  await apiClient.put(`/api/comm_networks/${id}`, data)
}

export const deleteNetwork = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/comm_networks/${id}`)
}
